import { useState, useEffect } from "react";
import { Modal } from "./Modal";
import { FormInput } from "./FormFields";
import { useCollaborationStore } from "../store/collaborationStore";
import type { PermissionLevel } from "@poi-planner/shared";

function buildInviteUrl(token: string): string {
  return `${window.location.origin}/invite/${token}`;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

interface ShareTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  tripName: string;
  isOwner: boolean;
}

function formatExpiryTime(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Expired";
  if (diffDays === 1) return "Expires tomorrow";
  if (diffDays < 7) return `Expires in ${diffDays} days`;
  return `Expires ${expiry.toLocaleDateString()}`;
}

export function ShareTripModal({
  isOpen,
  onClose,
  tripId,
  tripName,
  isOwner,
}: ShareTripModalProps) {
  const {
    shares,
    inviteLinks,
    loading,
    loadTripShares,
    loadInviteLinks,
    createInviteLink,
    cancelInviteLink,
    updatePermission,
    removeShare,
  } = useCollaborationStore();

  const [permission, setPermission] = useState<PermissionLevel>("editor");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // Filter to only show pending links
  const pendingLinks = inviteLinks.filter((link) => link.status === "pending");

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && tripId) {
      loadTripShares(tripId);
      loadInviteLinks(tripId);
      setPermission("editor");
      setLabel("");
      setError(null);
      setCreatedLink(null);
      setCopiedLinkId(null);
    }
  }, [isOpen, tripId, loadTripShares, loadInviteLinks]);

  const handleCreateLink = async () => {
    setError(null);
    setIsCreating(true);
    setCreatedLink(null);

    try {
      const url = await createInviteLink(
        tripId,
        permission,
        label.trim() || undefined,
      );
      setCreatedLink(url);
      setLabel("");
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to create invite link"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async (linkUrl: string, linkId?: string) => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      if (linkId) {
        setCopiedLinkId(linkId);
        setTimeout(() => setCopiedLinkId(null), 2000);
      }
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleCancelLink = async (linkId: string) => {
    try {
      await cancelInviteLink(linkId);
    } catch (err) {
      console.error("Failed to cancel link:", err);
    }
  };

  const handleUpdatePermission = async (
    shareId: string,
    newPermission: PermissionLevel,
  ) => {
    try {
      await updatePermission(shareId, newPermission);
    } catch (err) {
      console.error("Failed to update permission:", err);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      await removeShare(shareId);
    } catch (err) {
      console.error("Failed to remove share:", err);
    }
  };

  if (!isOwner) {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title="Share Trip"
        icon="share"
        size="lg"
      >
        <div className="text-center py-8 text-secondary">
          Only the trip owner can manage sharing settings.
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Share "${tripName}"`}
      icon="share"
      size="xl"
    >
      <div className="flex flex-col gap-7">
        {/* Create Invite Link Section */}
        <div>
          <div className="mb-4 flex items-center justify-between border-b-2 border-accent pb-3">
            <div className="flex flex-col gap-0.5">
              <h4 className="m-0 text-sm font-bold uppercase tracking-wide text-primary">
                Create Invite Link
              </h4>
              <p className="m-0 text-xs text-secondary">
                Generate a shareable link for anyone to join
              </p>
            </div>
            <span className="material-symbols-outlined text-xl text-accent">
              link
            </span>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              {/* Permission Select */}
              <div className="form-group flex-1">
                <label className="form-label">Permission</label>
                <select
                  value={permission}
                  onChange={(e) =>
                    setPermission(e.target.value as PermissionLevel)
                  }
                  className="select-base"
                >
                  <option value="editor">Editor - Can view and edit</option>
                  <option value="viewer">Viewer - Can only view</option>
                </select>
              </div>

              {/* Label Input */}
              <div className="form-group flex-1">
                <label className="form-label">Label (optional)</label>
                <FormInput
                  value={label}
                  onChange={setLabel}
                  placeholder="e.g., For mom, Work team"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 rounded-md border border-error/25 bg-error-bg px-3 py-2.5 text-sm font-medium text-error">
                <span className="material-symbols-outlined text-lg">error</span>
                <span>{error}</span>
              </div>
            )}

            {/* Created Link Display */}
            {createdLink && (
              <div className="rounded-[10px] border border-success/25 bg-gradient-to-br from-success-bg to-success-bg-strong p-3.5">
                <div className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-success">
                  <span className="material-symbols-outlined text-lg text-success">
                    check_circle
                  </span>
                  <span>Link created! Share it with your collaborator:</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={createdLink}
                    readOnly
                    className="min-w-0 flex-1 rounded-md border border-success/25 bg-white px-3 py-2.5 font-mono text-xs text-primary outline-none focus:border-success"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    onClick={() => handleCopyLink(createdLink)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border-none bg-success px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-success/90"
                  >
                    <span className="material-symbols-outlined text-base">
                      content_copy
                    </span>
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Create Button */}
            <button
              onClick={handleCreateLink}
              disabled={isCreating}
              className="flex items-center justify-center gap-2 rounded-lg border-none bg-accent px-5 py-3 font-sans text-sm font-bold text-white shadow-sm shadow-orange-500/20 transition-all hover:-translate-y-px hover:shadow-md hover:shadow-orange-500/30 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCreating ? (
                <>
                  <span className="size-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"></span>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">
                    add_link
                  </span>
                  <span>Create Link</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Active Invite Links Section */}
        {pendingLinks.length > 0 && (
          <div>
            <div className="mb-4 flex items-center justify-between border-b-2 border-accent pb-3">
              <div className="flex flex-col gap-0.5">
                <h4 className="m-0 text-sm font-bold uppercase tracking-wide text-primary">
                  Active Invite Links
                </h4>
              </div>
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-orange-50 px-2 text-xs font-bold text-accent">
                {pendingLinks.length}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {pendingLinks.map((link) => {
                const linkUrl = buildInviteUrl(link.token);
                const isCopied = copiedLinkId === link.id;

                return (
                  <div
                    key={link.id}
                    className="flex items-center justify-between rounded-lg border border-border bg-slate-50 px-3.5 py-3 transition-all hover:border-accent hover:bg-white hover:shadow-sm hover:shadow-orange-500/10"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-orange-50">
                        <span className="material-symbols-outlined text-lg text-accent">
                          link
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-col gap-1">
                        <div className="truncate text-sm font-semibold text-primary">
                          {link.label ||
                            `${link.permissionLevel === "editor" ? "Editor" : "Viewer"} link`}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex rounded px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${
                              link.permissionLevel === "editor"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {link.permissionLevel}
                          </span>
                          <span className="text-xs text-tertiary">
                            {formatExpiryTime(link.expiresAt)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleCopyLink(linkUrl, link.id)}
                        className={`flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          isCopied
                            ? "border-success/25 bg-success-bg text-success"
                            : "border-border bg-white text-secondary hover:border-secondary hover:bg-subtle hover:text-primary"
                        }`}
                        title="Copy link"
                      >
                        <span className="material-symbols-outlined text-sm">
                          {isCopied ? "check" : "content_copy"}
                        </span>
                        {isCopied ? "Copied!" : "Copy"}
                      </button>

                      <button
                        onClick={() => handleCancelLink(link.id)}
                        className="flex size-7 items-center justify-center rounded-md border border-border bg-transparent text-tertiary transition-colors hover:border-error/25 hover:bg-error-bg hover:text-error"
                        title="Cancel link"
                      >
                        <span className="material-symbols-outlined text-base">
                          close
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Collaborators List */}
        <div>
          <div className="mb-4 flex items-center justify-between border-b-2 border-accent pb-3">
            <div className="flex flex-col gap-0.5">
              <h4 className="m-0 text-sm font-bold uppercase tracking-wide text-primary">
                Collaborators
              </h4>
            </div>
            <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-orange-50 px-2 text-xs font-bold text-accent">
              {shares.length}
            </span>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
              <span className="size-6 animate-spin rounded-full border-3 border-secondary/20 border-t-secondary"></span>
              <p className="mt-3 text-sm text-secondary">
                Loading collaborators...
              </p>
            </div>
          ) : shares.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
              <div className="mb-3.5 flex size-14 items-center justify-center rounded-xl bg-orange-50">
                <span className="material-symbols-outlined text-3xl text-accent">
                  group
                </span>
              </div>
              <p className="m-0 mb-1 text-sm font-bold text-primary">
                No collaborators yet
              </p>
              <p className="m-0 text-sm text-secondary">
                Create an invite link above to share this trip
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {shares.map((share) => (
                <div
                  key={share.id}
                  className="flex items-center justify-between rounded-lg border border-border bg-white px-3.5 py-3 transition-all hover:border-accent hover:shadow-sm hover:shadow-orange-500/10"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-orange-50">
                      <span className="material-symbols-outlined text-lg text-accent">
                        person
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="truncate text-sm font-semibold text-primary">
                        {share.sharedWithUserId}
                      </div>
                      <div className="text-xs text-tertiary">
                        Added {new Date(share.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={share.permissionLevel}
                      onChange={(e) =>
                        handleUpdatePermission(
                          share.id,
                          e.target.value as PermissionLevel,
                        )
                      }
                      className="cursor-pointer rounded-md border border-transparent bg-subtle px-2.5 py-1.5 text-xs font-semibold text-primary outline-none transition-colors hover:border-secondary hover:bg-border focus:border-accent"
                    >
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>

                    <button
                      onClick={() => handleRemoveShare(share.id)}
                      className="flex size-7 items-center justify-center rounded-md border border-border bg-transparent text-tertiary transition-colors hover:border-error/25 hover:bg-error-bg hover:text-error"
                      title="Remove access"
                    >
                      <span className="material-symbols-outlined text-base">
                        close
                      </span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
